import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Target, Instagram, Youtube, Linkedin, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutUs: React.FC = () => {
  const { t } = useTranslation('aboutUs');
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const founders = [
    {
      name: 'Lea Anders',
      role: 'Co-Founder & CEO',
      bio: t('lea.bio'),
      image: '/images/Lea.png',
      linkedin: 'https://www.linkedin.com/in/leaanders/'
    },
    {
      name: 'Vu Ngo',
      role: 'Co-Founder & CTO',
      bio: t('vu.bio'),
      image: '/images/vu.jpg',
      linkedin: 'https://www.linkedin.com/in/minh-vu-ngo-050b6a171/'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 via-yellow-500/10 to-pink-500/10" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-5 sm:pb-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">
              <span className="bg-gradient-to-r from-pink-600 via-pink-500 to-yellow-500 bg-clip-text text-transparent">
                {t('title')}
              </span>
            </h1>
            
            <p className="text-sm sm:text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
              {t('subtitle')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Founders Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
          {founders.map((founder, index) => (
            <motion.div
              key={founder.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all border border-pink-100 p-4 sm:p-9"
            >
              <div className="flex flex-col items-center text-center mb-5 sm:mb-6">
                <div className="relative mb-3 sm:mb-4">
                    <div className="w-24 h-24 sm:w-40 sm:h-40 rounded-full overflow-hidden ring-3 sm:ring-4 ring-pink-200 shadow-md bg-pink-50 relative">
                    {!loadedImages.has(founder.name) && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-pink-200 border-t-pink-500 rounded-full animate-spin"></div>
                      </div>
                    )}
                    <img
                      src={founder.image}
                      alt={founder.name}
                      loading="eager"
                      onLoad={() => setLoadedImages(prev => new Set(prev).add(founder.name))}
                      className={`w-full h-full object-cover transition-opacity duration-300 ${loadedImages.has(founder.name) ? 'opacity-100' : 'opacity-0'}`}
                      style={founder.name === 'Lea Anders' ? { 
                        transform: 'scale(1.2)',
                        objectPosition: 'center center'
                      } : founder.name === 'Vu Ngo' ? {
                        transform: 'scale(1.9)',
                        objectPosition: 'center center'
                      } : {}}
                    />
                  </div>
                </div>

                <h3 className="text-base sm:text-2xl font-bold text-gray-900 mb-1 flex items-center justify-center gap-2">
                  {founder.name}
                  {founder.linkedin && (
                    <a 
                      href={founder.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:scale-110 transition-transform"
                    >
                      <Linkedin className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-blue-500 hover:text-blue-600" />
                    </a>
                  )}
                </h3>
                <p className="text-pink-600 font-semibold text-xs sm:text-base">
                  {founder.role}
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed text-left sm:text-justify whitespace-pre-line text-xs sm:text-base">
                {founder.bio}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Mission Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-br from-pink-500 via-pink-600 to-yellow-500 rounded-2xl shadow-md p-5 sm:p-12 text-center text-white relative overflow-hidden"
        >
          <div className="relative z-10">
            <h2 className="text-lg sm:text-3xl font-bold mb-3 sm:mb-5">
              {t('mission.title')}
            </h2>
            
            <p className="text-xs sm:text-base leading-relaxed max-w-3xl mx-auto text-white/95 text-left sm:text-justify mb-2 sm:mb-4">
              {t('mission.text1')}
            </p>
            <p className="text-xs sm:text-base leading-relaxed max-w-3xl mx-auto text-white/95 text-left sm:text-justify">
              {t('mission.text2')}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Join Our Mission */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3 className="text-lg sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
            {t('joinUs.title')}
          </h3>
          <Link
            to="/careers"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-semibold rounded-full hover:opacity-90 transition-opacity shadow-sm text-xs sm:text-base"
          >
            {t('joinUs.cta')}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>
      </div>

      {/* Social Media Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3 className="text-lg sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
            {t('social.title')}
          </h3>
          
          <div className="flex items-center justify-center gap-4 sm:gap-6">
            <a
              href="https://www.tiktok.com/@cevee.eu"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-pink-100 to-yellow-100 rounded-full hover:from-pink-200 hover:to-yellow-200 transition-all transform hover:scale-110 shadow-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="w-4 h-4 sm:w-7 sm:h-7 fill-current text-gray-800">
                <path d="M448 209.9a210.1 210.1 0 0 1 -122.8-39.3V349.4A162.6 162.6 0 1 1 185 188.3V278.2a74.6 74.6 0 1 0 52.2 71.2V0l88 0a121.2 121.2 0 0 0 1.9 22.2h0A122.2 122.2 0 0 0 381 102.4a121.4 121.4 0 0 0 67 20.1z"/>
              </svg>
            </a>
            <a
              href="https://www.instagram.com/cevee.eu/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-pink-100 to-yellow-100 rounded-full hover:from-pink-200 hover:to-yellow-200 transition-all transform hover:scale-110 shadow-sm"
            >
              <Instagram className="w-4 h-4 sm:w-7 sm:h-7 text-pink-600" />
            </a>
            <a
              href="https://youtube.com/@ceveeeu"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-pink-100 to-yellow-100 rounded-full hover:from-pink-200 hover:to-yellow-200 transition-all transform hover:scale-110 shadow-sm"
            >
              <Youtube className="w-4 h-4 sm:w-7 sm:h-7 text-red-600" />
            </a>
            <a
              href="https://www.linkedin.com/company/ceveeeu/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-pink-100 to-yellow-100 rounded-full hover:from-pink-200 hover:to-yellow-200 transition-all transform hover:scale-110 shadow-sm"
            >
              <Linkedin className="w-4 h-4 sm:w-7 sm:h-7 text-blue-600" />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutUs;

